import { PackageCheck } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import { Button, formatTimeAgo, type Column } from '@platform/design-system';
import type { Order, OrderStatus } from '../orders/orders.ts';
import { RETURNS_ROLE } from '../returns/returns.ts';
import { usePageContext } from '../usePageContext.ts';
import { ParcelWorklist, type ParcelWorklistProps } from './ParcelWorklist.tsx';
import { RETURN_STATUSES, type ReturnStatus } from './parcels.ts';

export type ReturnedParcelsPageProps = Omit<
  ParcelWorklistProps,
  'kind' | 'view' | 'statusFilter' | 'timingColumn' | 'action'
> & {
  status: ReturnStatus | null;
  onStatusChange: (status: ReturnStatus | null) => void;
  onReceive: () => void;
};

const returnStatusOf = (status: OrderStatus | null): ReturnStatus | null =>
  RETURN_STATUSES.find((candidate) => candidate === status) ?? null;

export const ReturnedParcelsPage = (props: ReturnedParcelsPageProps) => {
  const { status, onStatusChange, onReceive, ...worklist } = props;
  const { t, locale } = usePageContext();
  const canReceive = useAuth().hasRole(RETURNS_ROLE);
  const now = new Date();
  const returnedSince: Column<Order> = {
    id: 'returnedSince',
    header: t('parcels.returnedSince'),
    cell: (order) => formatTimeAgo(order.statusChangedAt, locale, now),
  };
  return (
    <ParcelWorklist
      {...worklist}
      kind="returned"
      view="returns"
      statusFilter={{
        value: status,
        options: RETURN_STATUSES,
        onChange: (next) => {
          onStatusChange(returnStatusOf(next));
        },
      }}
      timingColumn={returnedSince}
      action={
        canReceive ? (
          <Button icon={<PackageCheck size={20} aria-hidden="true" />} onClick={onReceive}>
            {t('parcels.receive')}
          </Button>
        ) : undefined
      }
    />
  );
};
